import React, { useState } from 'react'
import { useSelector } from 'react-redux'
import { Button, Spinner } from 'react-bootstrap'
import * as Yup from 'yup'
import { updateTeamMembers } from '../../../../services/manage'
import { getTeamMemberPriceIdBySymbol } from 'constants/plans'
import styles from './InviteTeammates.module.css'

const emailSchema = Yup.string().email('Please enter a valid email').required()

const REJECTION_REASON_MESSAGES = {
  TEAM_MEMBER_SELF_ADD: 'you cannot add yourself',
  TEAM_MEMBER_ALREADY_PAID: 'already has its own paid account',
  TEAM_MEMBER_ALREADY_IN_TEAM: 'already belongs to another team'
}

const describeRejection = ({ email, reason }) =>
  `${email} (${REJECTION_REASON_MESSAGES[reason] || 'could not be added'})`

const InviteTeammates = ({ className = '' }) => {
  const { userProfile, members } = useSelector((state) => state.manage)
  const existingTeamMembers = members?.map((m) => m.email) || []
  const [emails, setEmails] = useState([])
  const [currentInput, setCurrentInput] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [inputError, setInputError] = useState('')
  const [successMessage, setSuccessMessage] = useState('')
  const [errorMessage, setErrorMessage] = useState('')

  // Validate email format
  const validateEmail = async (email) => {
    try {
      await emailSchema.validate(email)
      setInputError('')
      return true
    } catch (error) {
      setInputError(error.message)
      return false
    }
  }

  const addEmail = async (email) => {
    const trimmedEmail = email.trim().toLowerCase()
    if (!trimmedEmail) return

    const isValid = await validateEmail(trimmedEmail)
    if (!isValid) return

    if (!emails.includes(trimmedEmail)) {
      setEmails([...emails, trimmedEmail])
      setCurrentInput('')
      setInputError('')
    } else {
      setInputError('Email already added')
    }
  }

  const removeEmail = (emailToRemove) => {
    setEmails(emails.filter((email) => email !== emailToRemove))
  }

  const handleSendInvites = async () => {
    if (emails.length === 0) return

    setIsLoading(true)
    setSuccessMessage('')
    setErrorMessage('')

    try {
      const teamMemberPriceId = getTeamMemberPriceIdBySymbol(userProfile?.currency)
      const request = {
        add: emails.filter((email) => !existingTeamMembers.includes(email)),
        remove: [],
        teamMemberPriceId
      }
      const { data } = await updateTeamMembers(request)

      const rejected = Array.isArray(data?.rejected) ? data.rejected : []
      const addedCount = Array.isArray(data?.added) ? data.added.length : 0
      let message = data?.manual
        ? "We've received your request and will update your account shortly"
        : addedCount > 0
          ? `Invite${addedCount > 1 ? 's' : ''} sent successfully to ${addedCount} email${addedCount > 1 ? 's' : ''}!`
          : 'No invites were sent.'
      if (rejected.length > 0) {
        message += ` Could not add: ${rejected.map(describeRejection).join(', ')}`
      }

      setSuccessMessage(message)

      setTimeout(() => {
        setEmails([])
        setCurrentInput('')
        setSuccessMessage('')
      }, 5000)
    } catch (error) {
      console.error('Failed to send invites:', error)
      setErrorMessage('Failed to send invites. Please try again.')

      setTimeout(() => {
        setErrorMessage('')
      }, 3000)
    } finally {
      setIsLoading(false)
    }
  }

  const handleKeyPress = (e) => {
    if ((e.key === 'Enter' || e.key === ',' || e.key === ' ') && currentInput.trim() !== '') {
      e.preventDefault()
      addEmail(currentInput)
    } else if (e.key === 'Backspace' && currentInput === '' && emails.length > 0) {
      removeEmail(emails[emails.length - 1])
    }
  }

  const handleInputChange = (e) => {
    const value = e.target.value
    setInputError('')

    if (value.endsWith(',') || value.endsWith(' ')) {
      const emailToAdd = value.slice(0, -1).trim()
      if (emailToAdd) {
        addEmail(emailToAdd)
      }
    } else {
      setCurrentInput(value)
    }
  }

  const handleInputBlur = () => {
    if (currentInput.trim() !== '') {
      addEmail(currentInput)
    }
  }

  // Check if button should be disabled
  const isButtonDisabled = () => {
    return isLoading || emails.length === 0
  }

  return (
    <div className={`${styles.inviteSection} ${className}`}>
      {/* Header */}
      <div className={styles.inviteHeader}>
        <h3 className={styles.inviteTitle}>Invite your Team</h3>
        <div className={styles.inviteIcon}>
          <img src="/assets/icons/team.svg" width="32" height="32" alt="Users" />
        </div>
      </div>

      <p className={styles.inviteDescription}>
        Collaborate with your team inside Tiiny Host. See your team&apos;s projects in one place.
      </p>

      <div className={styles.emailInputSection}>
        <label htmlFor="email-invite-input" className={styles.emailLabel}>
          Send an invite
        </label>
        <div className={styles.emailInputContainer}>
          <input
            id="email-invite-input"
            type="email"
            value={currentInput}
            onChange={handleInputChange}
            onKeyDown={handleKeyPress}
            onBlur={handleInputBlur}
            placeholder="Enter email address"
            className={`${styles.emailInput} ${inputError ? styles.inputError : ''}`}
          />

          {emails.length > 0 && (
            <div className={styles.emailTagsContainer}>
              {emails.map((email) => (
                <div key={email} className={styles.emailTag}>
                  <span className={styles.emailTagText}>{email}</span>
                  <button
                    type="button"
                    onClick={() => removeEmail(email)}
                    className={styles.removeEmailButton}
                    aria-label={`Remove ${email}`}
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {inputError && <div className={styles.errorMessage}>{inputError}</div>}
      </div>

      {/* Success/Error Messages */}
      {successMessage && <div className={styles.successMessage}>{successMessage}</div>}
      {errorMessage && <div className={styles.errorMessage}>{errorMessage}</div>}

      {/* Action Button */}
      <Button variant="outline-primary" onClick={handleSendInvites} disabled={isButtonDisabled()}>
        {isLoading ? <Spinner animation="grow" variant="secondary" size="sm" /> : 'Send invites'}
      </Button>
    </div>
  )
}

export default InviteTeammates
